"use client";

import Image from "next/image";
import React, { useState } from "react";
import {
  ToolbarWithBackButton,
  Button,
  PopupErrorClip,
  Popup10MinsWarning,
  PopupClipLimitExceed,
  PopupDifferentCouponTypeClipError,
} from "@/components";
import {
  isCouponNewCoupon,
  isPercentageDiscountCoupon,
  getOfferIsOutOfRange,
  isGachaOffer,
  validateCouponClip,
  filterValidBasketItems,
  getOfferEndDate,
  getClipEndDate,
  isSpecialCoupon,
  is10PercentOffCoupon,
} from "@/lib/util";
import { useGlobalStore } from "@/app/providers/ZustandProvider";
import {
  useFetchBasket,
  useAddToBasket,
  useRemoveFromBasket,
  useUpdateGachaPrize,
} from "@/lib/hooks";
import {
  useFetchMember,
  useFetchGachaOffers,
  useFetchHolders,
  useCurrentDate,
} from "@/lib/hooks";
import classNames from "classnames";
import { useRouter } from "next/navigation";

import { offer } from "@/generated";
import { light_offer_extended } from "@/lib/models/light_offer_extended";

interface OfferDetailsProps {
  offer: offer | light_offer_extended;
  onBack?: () => void;
  /** When true, no API calls or button actions; read-only preview. */
  previewMode?: boolean;
  gachaMode?: boolean;
  gachaAppId?: string;
  isUsed?: boolean;
  isFromUsedOffer?: boolean;
}

const RETAILER_ID = Number(process.env.NEXT_PUBLIC_RETAILER_ID) || 0;

const OfferDetails: React.FC<OfferDetailsProps> = ({
  offer,
  onBack,
  previewMode = false,
  gachaMode = false,
  isUsed = false,
  gachaAppId = "",
  isFromUsedOffer = false,
}) => {
  const router = useRouter();
  const memberId = useGlobalStore((s) => s.memberId);
  const accessToken = useGlobalStore((s) => s.accessToken);
  const setBasketOfferCountSnapshot = useGlobalStore(
    (s) => s.setBasketOfferCountSnapshot,
  );
  const now = useCurrentDate();
  const { data: basket = [] } = useFetchBasket(
    previewMode ? 0 : (memberId ?? 0),
  );
  const { mutate: addToBasket, isPending: isAdding } = useAddToBasket();
  const { mutate: removeFromBasket, isPending: isRemoving } =
    useRemoveFromBasket();
  const { mutate: updateGachaPrize, isPending: isGachaUpdating } =
    useUpdateGachaPrize();

  const [isRedirecting, setIsRedirecting] = useState(false);

  // Redirect if checkout session is in progress (barcode timer is active or unsettled)
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const endTimeStr = localStorage.getItem("barcode_timer_end_time");
      if (endTimeStr) {
        setTimeout(() => setIsRedirecting(true), 0);
        router.replace("/barcode");
      }
    }
  }, [router]);

  // Fetch member and gacha offers for validation
  const { data: member } = useFetchMember(memberId as number);
  const appId = member?.encrypted_app_id || ""; //
  const { data: holders = [] } = useFetchHolders(Number(memberId));
  const fspNbr = holders[0]?.ref || "";
  const { data: gachaOffers = [] } = useFetchGachaOffers(appId, accessToken, {
    enabled: !!appId && !!accessToken && !!fspNbr,
  });

  const [saveErrorOpen, setSaveErrorOpen] = useState(false);
  const [openMaxCouponsPopup, setOpenMaxCouponsPopup] = useState(false);
  const [isTimerWarningOpen, setIsTimerWarningOpen] = useState(false);
  const [openConflictingCouponsPopup, setOpenConflictingCouponsPopup] =
    useState(false);

  const filteredBasket = filterValidBasketItems(basket, now);
  const activeClippedGachaCount = gachaOffers.filter(
    (gachaOffer) => gachaOffer.keep === 1 && gachaOffer.used === 0,
  ).length;
  const rewardInBasket = filteredBasket.find((r) => r.offer?.id === offer.id);
  const gachaKeep = (offer as light_offer_extended).keep;
  const isClipped = gachaMode ? gachaKeep === 1 : !!rewardInBasket;
  const isSaveButtonDisabled = gachaMode
    ? isGachaUpdating
    : isAdding || isRemoving;

  const isOfferGachaOffer = isGachaOffer(offer);

  const handleClip = (options?: {
    onClipApiSuccess?: () => void;
  }): {
    isSuccess: boolean;
    error?:
      | "special_day_coupon_conflict"
      | "10_percent_off_coupon_conflict"
      | "max_coupons_limit_reached"
      | "multiple_coupons_of_the_same_type"
      | "error";
  } => {
    if (!memberId) return { isSuccess: false, error: "error" };
    //filtered basket to only include valid coupons (exclude expired and out-of-range special coupons)
    const validation = validateCouponClip(offer, filteredBasket, gachaOffers);

    if (!validation.isValid) {
      // Handle different error types
      if (validation.error === "max_coupons_limit_reached") {
        setOpenMaxCouponsPopup(true);
      } else if (
        validation.error === "multiple_special_day_coupons" ||
        validation.error === "multiple_10_percent_off_coupons"
      ) {
        setSaveErrorOpen(true);
        return { isSuccess: false, error: "multiple_coupons_of_the_same_type" };
      } else if (
        validation.error === "special_day_coupon_conflict" ||
        validation.error === "10_percent_off_coupon_conflict"
      ) {
        setOpenConflictingCouponsPopup(true);
      }
      return { isSuccess: false, error: validation.error };
    }
    if (gachaMode) {
      if (!gachaAppId || !accessToken) {
        setSaveErrorOpen(true);
        return { isSuccess: false, error: "error" };
      }
      updateGachaPrize(
        {
          userPrizeId: offer.id,
          keep: 1,
          appId: gachaAppId,
          accessToken,
        },
        {
          onError: () => setSaveErrorOpen(true),
          onSuccess: options?.onClipApiSuccess,
        },
      );
      return { isSuccess: true, error: undefined };
    }

    addToBasket(
      {
        memberId: Number(memberId),
        items: [{ offer_id: offer.id, retailer_id: RETAILER_ID }],
      },
      {
        onError: () => setSaveErrorOpen(true),
        onSuccess: options?.onClipApiSuccess,
      },
    );
    return { isSuccess: true, error: undefined };
  };

  const handleUnclip = () => {
    if (!memberId) return;
    if (gachaMode) {
      if (!gachaAppId || !accessToken) {
        setSaveErrorOpen(true);
        return;
      }
      updateGachaPrize(
        {
          userPrizeId: offer.id,
          keep: 0,
          appId: gachaAppId,
          accessToken,
        },
        { onError: () => setSaveErrorOpen(true) },
      );
      return;
    }
    if (!rewardInBasket) return;
    removeFromBasket(
      { memberId: Number(memberId), rewardId: rewardInBasket.id },
      {
        onError: () => setSaveErrorOpen(true),
      },
    );
  };

  const handleSaveButtonClick = () => {
    if (previewMode) return;
    if (isClipped) handleUnclip();
    else handleClip();
  };

  // Format dates for display
  const isSpecialOrPercentOff =
    isSpecialCoupon(offer as light_offer_extended) ||
    is10PercentOffCoupon(offer as light_offer_extended);
  const clippableDates = (offer as light_offer_extended).clippable_dates;

  const start =
    isSpecialOrPercentOff &&
    Array.isArray(clippableDates) &&
    clippableDates.length > 0
      ? new Date(clippableDates[0])
      : new Date(offer.started_at ?? now);
  const startDate = start.getDate();
  const startMonth = start.getMonth() + 1;

  const end = isFromUsedOffer
    ? getClipEndDate(offer, now)
    : getOfferEndDate(offer, now);
  const endDate = end.getDate();
  const endMonth = end.getMonth() + 1;

  const isNew =
    isCouponNewCoupon(offer, now) ||
    (offer as { is_new?: boolean }).is_new === true;
  const isPercentageDiscount = isPercentageDiscountCoupon(offer);
  const isOneTimeOnly = (offer.user_quota ?? 1) > 1 ? false : true;

  const saveButtonLabel = isClipped ? "保存済" : "保存";

  const isOutOfRange = getOfferIsOutOfRange(offer, now);

  if (isRedirecting) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-ng-background">
        <div className="border-ng-main border-t-transparent rounded-full animate-spin h-8 w-8 border-4" />
      </div>
    );
  }

  return (
    <div className="p-4 bg-ng-background">
      {/* Timer Warning */}
      <Popup10MinsWarning
        onPrimaryClick={() => {
          setBasketOfferCountSnapshot(
            filteredBasket.length + activeClippedGachaCount,
          );
          setIsTimerWarningOpen(false);
          router.replace("/barcode");
        }}
        onSecondaryClick={() => {
          setIsTimerWarningOpen(false);
        }}
        isOpen={isTimerWarningOpen}
        onClose={() => setIsTimerWarningOpen(false)}
      />
      <PopupErrorClip
        onPrimaryClick={() => setSaveErrorOpen(false)}
        isOpen={saveErrorOpen}
        onClose={() => setSaveErrorOpen(false)}
      />
      <PopupClipLimitExceed
        onPrimaryClick={() => setOpenMaxCouponsPopup(false)}
        isOpen={openMaxCouponsPopup}
        onClose={() => setOpenMaxCouponsPopup(false)}
      />
      <PopupDifferentCouponTypeClipError
        onPrimaryClick={() => setOpenConflictingCouponsPopup(false)}
        isOpen={openConflictingCouponsPopup}
        onClose={() => setOpenConflictingCouponsPopup(false)}
      />
      <div className="mx-auto max-w-md bg-ng-background min-h-screen pb-28 text-ng-font-1 rounded-lg ">
        <ToolbarWithBackButton onBack={onBack} />

        <div>
          <div className="flex justify-center mb-6">
            <div className="relative w-full max-w-85.75">
              {isFromUsedOffer && (
                <div className="offer-expired-overlay absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center z-20 bg-white/90 pointer-events-none">
                  <span className="offer-expired-text text-lg font-bold text-gray-600">
                    利用済
                  </span>
                </div>
              )}
              <Image
                src={
                  (isFromUsedOffer
                    ? offer.picture_url
                    : isOutOfRange
                      ? offer.unavailable_clip_image_url
                      : offer.picture_url) || ""
                }
                alt={offer.title}
                height={343}
                className="w-full h-auto max-h-85.75"
                loading="eager"
                width={343}
              />
            </div>
          </div>

          {(!isOutOfRange || isFromUsedOffer) && (
            <div className="flex flex-col items-center min-[360px]:flex-row min-[360px]:items-center w-full gap-2 mb-2 text-sm">
              <div className="flex items-center gap-2">
                {isNew && (
                  <span className="px-2 py-0.5 border-2 border-ng-red text-ng-red font-bold rounded-sm">
                    NEW
                  </span>
                )}
                <span className="px-2 py-0.5 border-2 border-ng-red text-ng-red font-bold rounded-sm">
                  {isOneTimeOnly ? "1回のみ" : "何回でも"}
                </span>
              </div>
              <div className="min-[360px]:ml-auto font-medium text-ng-font">
                有効期間:{" "}
                {isOfferGachaOffer
                  ? `${endMonth}/${endDate} ${end.getHours()}:${String(end.getMinutes()).padStart(2, "0")}まで`
                  : `${startMonth}/${startDate}〜${endMonth}/${endDate}`}
              </div>
            </div>
          )}

          {/* Discount Amount */}
          {(!isOutOfRange || isFromUsedOffer) && (
            <div className="text-center text-ng-main font-bold text-3xl py-6">
              {isOfferGachaOffer ? (
                <span>{offer.title}</span>
              ) : (
                <span>
                  {offer.discount_value}
                  {isPercentageDiscount ? "%引き" : "円引き"}
                </span>
              )}
            </div>
          )}

          {/* Title */}
          {(!isOutOfRange || isFromUsedOffer) && !isOfferGachaOffer && (
            <h1
              className="text-font-3 font-bold text-lg mb-3 leading-tight min-w-0 break-all"
              title={offer.title ?? undefined}
            >
              {offer.title}
            </h1>
          )}

          {(!isOutOfRange || isFromUsedOffer) && !isOfferGachaOffer && (
            <div className="text-sm font-medium text-ng-font-1">
              {offer.subtitle}
            </div>
          )}

          {/* Gray Availability Box */}
          {(!isOutOfRange || isFromUsedOffer) && (
            <div className="bg-gray-100 text-font text-xs text-center py-2 px-4 mb-6 rounded-sm">
              一部取り扱いのない店舗がございます
            </div>
          )}

          {offer.bl_message_ref && (
            <div className="text-[16px] font-bold my-6 text-ng-font-3">
              ご利用条件
            </div>
          )}
          {!isOfferGachaOffer && (
            <div
              className="mb-2 text-ng-font-3 text-[16px]"
              dangerouslySetInnerHTML={{
                __html:
                  isOutOfRange && !isFromUsedOffer
                    ? offer.unavailable_clip_text || ""
                    : offer.overview_html || "",
              }}
            />
          )}
          {offer.bl_message_ref && (
            <div className="text-[16px] font-bold my-6 text-ng-font-3">
              ご利用上の注意
            </div>
          )}
          {(!isOutOfRange || isFromUsedOffer) && (
            <div
              className="mb-2 text-ng-font-3 text-[16px]"
              dangerouslySetInnerHTML={{
                __html: offer.bl_message_ref
                  ? (offer as offer).partner_disclaimer_html || ""
                  : offer.disclaimer_html || "",
              }}
            />
          )}

          <div className="px-4.5 flex items-center justify-between gap-2 sm:gap-4 pb-4.5 fixed bottom-0 left-0 right-0 w-full mx-auto max-w-md sm:max-w-lg md:max-w-xl">
            {isFromUsedOffer ? (
              <>
                <div className="min-w-0 min-[371px]:flex-1 max-w-[140px]">
                  <Button
                    className={
                      "w-full rounded-4xl px-4 sm:px-5 gap-2 sm:gap-3 min-w-0 whitespace-nowrap"
                    }
                    label={"保存"}
                    variant="outlined"
                    disabled={
                      isSaveButtonDisabled || isFromUsedOffer || isOutOfRange
                    }
                    icon={
                      <Image
                        src={"/icons/hand-gray.png"}
                        alt="Hand"
                        width={24}
                        height={24}
                        className="h-auto"
                      />
                    }
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <Button
                    className="w-full min-w-0 rounded-4xl"
                    disabled={true}
                    label="クーポンを利用する"
                    variant="outlined"
                  />
                </div>
              </>
            ) : !isOutOfRange ? (
              <>
                <div className="min-w-0 min-[371px]:flex-1 max-w-[140px]">
                  <Button
                    className={classNames(
                      "w-full rounded-4xl px-4 sm:px-5 gap-2 sm:gap-3 min-w-0 whitespace-nowrap",
                      isClipped &&
                        !isUsed &&
                        !isOutOfRange &&
                        "bg-[#9F9F9F]! border-[#9F9F9F]! text-[#CACACA]!",
                    )}
                    label={saveButtonLabel}
                    variant="outlined"
                    disabled={isSaveButtonDisabled || isUsed || isOutOfRange}
                    icon={
                      isSaveButtonDisabled ? (
                        <div className="border-ng-main border-t-transparent rounded-full animate-spin h-4.5 w-4.5 border-2" />
                      ) : (
                        <Image
                          src={
                            isClipped || isUsed || isOutOfRange
                              ? "/icons/hand-gray.png"
                              : "/icons/hand.png"
                          }
                          alt="Hand"
                          width={24}
                          height={24}
                          className="h-auto"
                        />
                      )
                    }
                    trailingIcon={
                      isClipped ? (
                        <Image
                          src="/icons/x.png"
                          alt="Remove"
                          width={16}
                          height={16}
                          className="h-4 w-4"
                        />
                      ) : undefined
                    }
                    onClick={handleSaveButtonClick}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <Button
                    className="w-full min-w-0 rounded-4xl"
                    disabled={isUsed || isOutOfRange}
                    label="クーポンを利用する"
                    variant="outlined"
                    onClick={() => {
                      if (previewMode) return;
                      if (!isClipped) {
                        const clipResult = handleClip({
                          onClipApiSuccess: () => setIsTimerWarningOpen(true),
                        });
                        if (!clipResult.isSuccess) {
                          if (
                            clipResult.error ===
                            "multiple_coupons_of_the_same_type"
                          ) {
                            setSaveErrorOpen(true);
                          }
                          return;
                        }
                        return;
                      }
                      setIsTimerWarningOpen(true);
                    }}
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferDetails;
